import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useOutletContext, useParams, useSearchParams } from 'react-router-dom';

import { ORDER_STATUS_COLORS, ORDER_STATUS_STRINGS } from '@domain/orderStatus';
import {
  PRODUCT_STATUS_COLORS,
  PRODUCT_STATUS_STRINGS,
} from '@domain/productStatus';
import {
  Badge,
  Center,
  Group,
  Image,
  Loader,
  Stepper,
  Text,
  Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { dateToDateString } from '@utils/date';
import { priceToDecimal } from '@utils/price';

import { OrderProduct } from '@interfaces/orders.interface';

import {
  GetOrdersRequestParams,
  GetOrdersSortBy,
  useGetOrderProductsCountQuery,
  useGetOrderProductsQuery,
  useGetSingleAffiliatedOrderQuery,
} from '@api/orders.api';

import Card from '@components/card/Card';
import { LayoutContextType } from '@components/layout/Layout';
import SearchBar from '@components/SearchBar';

import Forbidden from './Forbidden';
import NotFound from './NotFound';

const statusIndexMap = {
  pending: 0,
  inserted: 0,
  waitingConfirmation: 1,
  waitingPayment: 2,
  confirmed: 3,
  production: 4,
  closed: 5,
};

export default function AffiliatedOrderDetail() {
  // ==========================================================================
  // General
  // ==========================================================================
  const { id } = useParams();

  const [searchParams] = useSearchParams();

  // ==========================================================================
  // State
  // ==========================================================================
  const [filters, setFilters] = useState<GetOrdersRequestParams>({
    page: +(searchParams.get('page') || 1),
    pageLength: +(searchParams.get('pageLength') || 5),
    searchQuery: searchParams.get('search') || undefined,
    sortBy: 'id',
    sortOrder: 'asc',
  });

  const [searchQuery] = useDebouncedValue(filters.searchQuery, 200, {
    leading: true,
  });

  const [sortStatus, setSortStatus] = useState<
    DataTableSortStatus<OrderProduct>
  >({
    columnAccessor: 'name',
    direction: 'asc',
  });

  useEffect(() => {
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as GetOrdersSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================
  const {
    data: order,
    isLoading,
    error,
  } = useGetSingleAffiliatedOrderQuery(id!);

  // Get initial products count
  const {
    data: productsCount = { count: 0 },
    isLoading: isLoadingProductsCount,
  } = useGetOrderProductsCountQuery({ id: id!, searchQuery });

  const { data: productsData = [], isLoading: isProductsLoading } =
    useGetOrderProductsQuery({
      id: id!,
      params: { ...filters, searchQuery },
    });

  // ==========================================================================
  // Render
  // ==========================================================================

  const totalPriceNoDiscount = order?.total || 0;

  const totalPrice =
    totalPriceNoDiscount *
    (order?.paymentType !== 'onlineOnConfirm' ? 0.97 : 1);

  const stepperSteps = Object.keys(ORDER_STATUS_STRINGS)
    .filter((status) => status !== 'processingPayment')
    .map((status) => (
      <Stepper.Step
        key={status}
        label={
          ORDER_STATUS_STRINGS[status as keyof typeof ORDER_STATUS_STRINGS]
        }
        color={ORDER_STATUS_COLORS[status as keyof typeof ORDER_STATUS_STRINGS]}
      />
    ));

  const { setLayoutProps } = useOutletContext<LayoutContextType>();

  useLayoutEffect(() => {
    setLayoutProps({
      title: `Dettagli ordine #${id}`,
      backLink: { title: 'Lista ordini', to: '/ordini-affiliati' },
    });
  }, [setLayoutProps, id]);

  return isLoading || isProductsLoading || isLoadingProductsCount ? (
    <Center my="xl">
      <Loader />
    </Center>
  ) : !isLoading &&
    (!order || (error && (error as FetchBaseQueryError).status === 404)) ? (
    <NotFound />
  ) : !isLoading &&
    (!order || (error && (error as FetchBaseQueryError).status === 403)) ? (
    <Forbidden />
  ) : (
    <>
      <>
        <Stepper
          visibleFrom="sm"
          my={40}
          active={
            statusIndexMap[
              order?.status
                ? order.status !== 'processingPayment'
                  ? order.status !== 'pending'
                    ? order.status !== 'refunded'
                      ? order.status
                      : 'inserted'
                    : 'closed'
                  : 'waitingPayment'
                : 'inserted'
            ]
          }
          size="sm"
          styles={{
            step: {
              flexDirection: 'column',
            },
            stepBody: {
              marginLeft: '0',
              marginTop: '7px',
            },
          }}
        >
          {stepperSteps}
        </Stepper>

        <Group grow align="start">
          <Card title="Informazioni ordine">
            <Group justify="space-between">
              <Text fw="bold">Numero ordine</Text>
              <Text>{order!.id}</Text>
            </Group>
            <Group justify="space-between">
              <Text fw="bold">Cliente</Text>
              <Text>{order!.invoicingName}</Text>
            </Group>
            <Group justify="space-between">
              <Text fw="bold">Riferimento cliente</Text>
              <Text>{order!.customerRef || '-'}</Text>
            </Group>
            <Group justify="space-between">
              <Text fw="bold">Data</Text>
              <Text>{dateToDateString(new Date(order!.date))}</Text>
            </Group>
          </Card>

          <Card
            title={`Indirizzo di ${order!.shippingMethod === 'courier' ? 'consegna' : 'ritiro'}`}
          >
            {order!.shippingMethod === 'courier' ? (
              <>
                <Text>
                  {order!.shippingZip} - {order!.shippingCity} (
                  {order!.shippingProvince}), {order!.shippingNation}
                </Text>
              </>
            ) : (
              <>
                <Text>Via dell'Artigianato, 41/A</Text>
                <Text>36030 - Villaverla (VI), Italia</Text>
              </>
            )}
          </Card>

          <Card title="Spedizione e pagamento">
            <Group justify="space-between">
              <Text fw="bold">Tipologia spedizione</Text>
              <Text>
                {order!.shippingMethod === 'courier'
                  ? 'Corriere'
                  : 'Ritiro in sede'}
              </Text>
            </Group>

            {order!.shippingMethod === 'courier' && (
              <>
                <Group justify="space-between">
                  <Text fw="bold">Data spedizione prevista</Text>
                  <Text>
                    {order!.shippingDate
                      ? dateToDateString(new Date(order!.shippingDate))
                      : 'Non disponibile'}
                  </Text>
                </Group>
              </>
            )}
            <Group justify="space-between">
              <Text fw="bold">Tipologia pagamento</Text>
              <Text>
                {order!.paymentType === 'onlineOnConfirm' ? (
                  'Pagamento online'
                ) : (
                  <Group style={{ display: 'inline-flex' }} gap="xs">
                    Bonifico bancario
                  </Group>
                )}
              </Text>
            </Group>
            <Group justify="space-between">
              <Text fw="bold">Totale (Iva In.)</Text>
              <Text ta="right">
                {order!.status === 'inserted' ||
                order!.status === 'waitingConfirmation' ||
                order!.status === 'waitingPayment' ? (
                  <>
                    € {priceToDecimal(totalPriceNoDiscount)}
                    <br />
                    {order!.paymentType !== 'onlineOnConfirm' && (
                      <>(€ {priceToDecimal(totalPrice)} pagando online)</>
                    )}
                  </>
                ) : order!.total !== totalPriceNoDiscount ? (
                  <>
                    <span style={{ textDecoration: 'line-through' }}>
                      € {priceToDecimal(totalPriceNoDiscount)}
                    </span>{' '}
                    {order!.paymentType !== 'onlineOnConfirm' && (
                      <>€ {priceToDecimal(order!.total)}</>
                    )}
                  </>
                ) : (
                  <>€ {priceToDecimal(totalPriceNoDiscount)}</>
                )}
              </Text>
            </Group>
            {order!.refundedAmount && (
              <Group justify="space-between">
                <Text fw="bold">Importo rimborsato</Text>
                <Text ta="right">
                  € {priceToDecimal(order!.refundedAmount)}
                </Text>
              </Group>
            )}
            <Group justify="space-between">
              <Text fw="bold">Provvigione</Text>
              <Text ta="right">
                € {priceToDecimal(order!.sellerCommission || 0)}
              </Text>
            </Group>
          </Card>
        </Group>
        <Title order={3} mt="lg">
          Prodotti
        </Title>
        <Group mt="lg" mb="md">
          <SearchBar
            placeholder="Ricerca per codice o nome prodotto"
            value={filters.searchQuery}
            onChange={(newValue) =>
              setFilters({ ...filters, searchQuery: newValue })
            }
          />
        </Group>
        <DataTable
          striped
          withTableBorder
          fetching={isProductsLoading || isLoadingProductsCount}
          noRecordsText="Nessun prodotto trovato"
          minHeight={productsData.length === 0 ? 200 : undefined}
          columns={[
            {
              accessor: 'imageUrl',
              title: '',
              width: '60px',
              render: (record) => (
                <Image
                  src={record.imageUrl}
                  alt={record.name}
                  h="2rem"
                  w="2rem"
                  fit="contain"
                />
              ),
            },
            {
              accessor: 'sku',
              title: 'Codice',
              sortable: true,
            },
            {
              accessor: 'name',
              title: 'Nome',
              sortable: true,
            },
            {
              accessor: 'color',
              title: 'Colore',
              render: (record) => record.color || '-',
            },
            {
              accessor: 'size',
              title: 'Taglia',
              render: (record) => record.size || '-',
            },
            {
              accessor: 'quantity',
              title: 'Qta.',
            },
            {
              accessor: 'totalPrice',
              title: 'Prezzo tot.',
              render: (record) => (
                <Group gap={3} wrap="nowrap">
                  <span>€</span>
                  {record.salePercent && (
                    <span>
                      <span style={{ textDecoration: 'line-through' }}>
                        {priceToDecimal(record.totalPrice)}
                      </span>
                    </span>
                  )}
                  <span>
                    {priceToDecimal(
                      record.totalPrice *
                        (1 - (record.salePercent || 0) / 1000),
                    )}
                  </span>
                </Group>
              ),
            },
            {
              accessor: 'salePercent',
              title: 'Sconto',
              render: (record) =>
                record.salePercent ? `${record.salePercent / 10}%` : '-',
            },
            {
              accessor: 'shippingDate',
              title: 'Data sped. prev.',
              render: (record) =>
                record.shippingDate
                  ? new Date(record.shippingDate).toLocaleDateString('IT-it')
                  : '-',
            },
            {
              accessor: 'status',
              title: 'Stato',
              render: (record) => (
                <Badge color={PRODUCT_STATUS_COLORS[record.status]}>
                  {PRODUCT_STATUS_STRINGS[record.status]}
                </Badge>
              ),
            },
          ]}
          records={productsData}
          sortStatus={sortStatus}
          onSortStatusChange={setSortStatus}
          page={filters.page!}
          recordsPerPage={filters.pageLength!}
          totalRecords={productsCount.count}
          paginationText={() => ''}
          recordsPerPageLabel=""
          recordsPerPageOptions={[5, 10, 20, 50]}
          onPageChange={(newPage) => setFilters({ ...filters, page: newPage })}
          onRecordsPerPageChange={(newPageLength) =>
            setFilters({ ...filters, pageLength: newPageLength, page: 1 })
          }
        />
      </>
    </>
  );
}
